import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Check, ArrowRight } from "lucide-react";

const benefits = [
  ["No monthly fees", "List for free, forever. We don't believe in barriers for makers."],
  ["Only 8% commission", "Half of what big platforms charge. Keep more of what you earn."],
  ["Featured seller opportunities", "Get spotlighted on the Alteza homepage and category pages."],
  ["Social media promotion", "We share your story and launches with our community."],
  ["Founding Seller benefits", "Lifetime perks, priority support, and a badge that lasts forever."],
  ["Canadian-based, worldwide reach", "Real humans you can email. Customers around the globe."],
];

export default function BecomeSeller() {
  const { user } = useAuth();
  return (
    <div data-testid="become-seller">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-5">◦ For Makers &amp; Founders</div>
            <h1 className="font-serif text-5xl lg:text-7xl tracking-tight leading-[1.02] font-medium">
              Grow Your Business<br /><em className="text-primary">your way.</em>
            </h1>
            <p className="mt-8 text-muted-foreground leading-relaxed max-w-md text-lg">
              Alteza is a curated marketplace built for female-owned, handmade brands.
              Candles, skincare, bath &amp; body, beauty, jewelry — your shop, your story, our community.
            </p>
            <Link
              to={user ? "/seller" : "/register?role=seller"}
              data-testid="open-studio-btn"
              className="btn-primary inline-flex items-center gap-2 mt-10 px-8 py-4 text-sm rounded-sm"
            >
              Open my shop <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-4 text-xs text-muted-foreground">Free to start. No card required.</p>
          </div>
          <div className="lg:col-span-6">
            <img
              src="https://images.pexels.com/photos/4202924/pexels-photo-4202924.jpeg?auto=compress&cs=tinysrgb&w=1200"
              alt="Female maker pouring candles"
              className="w-full aspect-[4/5] object-cover rounded-sm grain"
            />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section id="benefits" className="bg-secondary/50 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-4">◦ Why Alteza</div>
          <h2 className="font-serif text-4xl lg:text-5xl tracking-tight max-w-2xl">Built for makers, not for venture capital.</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map(([h, b]) => (
              <div key={h} className="bg-card border border-border p-7 rounded-sm">
                <Check className="w-6 h-6 text-primary mb-4" />
                <div className="font-semibold text-base">{h}</div>
                <p className="text-sm text-muted-foreground mt-2.5 leading-relaxed">{b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-4">◦ How it works</div>
        <h2 className="font-serif text-4xl lg:text-5xl tracking-tight max-w-2xl">Start selling in under ten minutes.</h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            ["01", "Apply", "Create your free account and tell us about your brand."],
            ["02", "List your handmade collection", "Upload photos, set your prices, and write your story."],
            ["03", "Start selling", "Customers find you, you ship — we handle payments via Stripe."],
          ].map(([n, h, b]) => (
            <div key={n} className="border-t border-border pt-6">
              <div className="font-serif text-5xl text-primary">{n}</div>
              <div className="font-serif text-2xl mt-4">{h}</div>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
        <div className="mt-16">
          <Link
            to={user ? "/seller" : "/register?role=seller"}
            className="btn-primary inline-flex items-center gap-2 px-8 py-4 text-sm rounded-sm"
          >
            Apply now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
